import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import { getAuth, Auth, User, createUserWithEmailAndPassword, signInWithEmailAndPassword, sendPasswordResetEmail, updatePassword, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, Firestore, doc, collection, collectionGroup, getDoc, getDocs, setDoc, updateDoc, deleteDoc, deleteField, query, where, orderBy, onSnapshot, Query, DocumentData } from 'firebase/firestore';
import { getFunctions, httpsCallable, Functions } from 'firebase/functions';
import { getStorage, ref as storageRef, uploadBytes, deleteObject, listAll } from 'firebase/storage';
import {
  AUDIOPATH,
  AUDIOMETAPATH,
  USERSPATH,
  GENRESPATH,
  ORGANIZATIONSPATH,
  CLIENTPATH,
  AGENCYPATH,
  GLOBAL_ARTISTPATH,
  PUBLIC_CATALOGUEPATH,
  getOrgCollectionPath,
  getOrgStoragePath,
} from '../../constants/paths';

// Type definitions
type OrgRole = 'ADMIN' | 'OPERATOR' | 'USER';

interface InviteData {
  id: string;
  code: string;
  orgId: string;
  orgName: string;
  role: OrgRole;
  createdBy: string;
  createdAt: string;
  expiresAt: string;
  used: boolean;
  usedBy: string | null;
  usedAt: string | null;
  invitedEmail: string;
}

const firebaseConfig = {
  apiKey: process.env.REACT_APP_API_KEY,
  authDomain: process.env.REACT_APP_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_DATABASE_URL,
  projectId: process.env.REACT_APP_PROJECT_ID,
  storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_APP_ID,
};


// Initialize Firebase
let app: FirebaseApp;
if (!getApps().length) {
  app = initializeApp(firebaseConfig);
} else {
  app = getApps()[0];
}

class Firebase {
  auth: Auth;
  db: Firestore;
  storage: ReturnType<typeof getStorage>;
  functions: Functions;
  // Some Cloud Functions live in a different region because they were newly
  // created after the multi-region Firestore rule change. This client
  // targets those functions.
  functionsWest1: Functions;
  storageRef: ReturnType<typeof storageRef>;
  audioRef: ReturnType<typeof storageRef>;
  cache: any = {};

  constructor() {
    this.auth = getAuth(app);
    this.db = getFirestore(app);
    this.storage = getStorage(app);
    this.functions = getFunctions(app, 'europe-west3');
    this.functionsWest1 = getFunctions(app, 'europe-west1');
    this.storageRef = storageRef(this.storage);
    this.audioRef = storageRef(this.storage, AUDIOPATH);
  }

  // *** Auth API *** //
  doCreateUserWithEmailAndPassword = (email: string, password: string) =>
    createUserWithEmailAndPassword(this.auth, email, password);

  doSignInWithEmailAndPassword = (email: string, password: string) =>
    signInWithEmailAndPassword(this.auth, email, password);

  doSignOut = () => this.auth.signOut();

  doPasswordReset = (email: string) => sendPasswordResetEmail(this.auth, email);

  doPasswordUpdate = (password: string) =>
    this.auth.currentUser ? updatePassword(this.auth.currentUser, password) : Promise.reject('No current user');

  onAuthUserListener = (next: any, fallback: any) =>
    onAuthStateChanged(this.auth, async (authUser: User | null) => {
      if (authUser) {
        try {
          // Force refresh the ID token to ensure Firestore has valid auth
          await authUser.getIdToken(true);

          const userDoc = doc(this.db, USERSPATH, authUser.uid);
          let docSnap = await getDoc(userDoc);
          let dbUser;
          if (!docSnap.exists()) {
            dbUser = {
              roles: {
                USER: 'USER',
              },
              email: authUser.email,
              createdAt: new Date().toISOString(),
            };
            await setDoc(userDoc, dbUser);
          } else {
            dbUser = docSnap.data();
            if (!dbUser.roles) {
              dbUser.roles = { USER: 'USER' };
            } else if (!dbUser.roles.USER) {
              dbUser.roles.USER = 'USER';
            }
          }
          const mergedUser = {
            uid: authUser.uid,
            email: authUser.email,
            ...dbUser,
          };
          next(mergedUser);
        } catch (error) {
          console.error('Error getting user document:', error);
          fallback();
        }
      } else {
        fallback();
      }
    });

  // *** User API *** //
  user = (uid: string) => doc(this.db, USERSPATH, uid);

  getUserDetails = async (uid: string) => {
    const userDoc = await getDoc(doc(this.db, USERSPATH, uid));
    if (userDoc.exists()) {
      return userDoc.data();
    }
    return null;
  };

  deleteUser = (uid: string) => {
    const callable = httpsCallable(this.functions, 'deleteUser');
    return callable({ uid })
      .then((result: any) => {
        return result;
      })
      .catch((error: any) => {
        console.error('Function execution failed:', error);
        throw error;
      });
  };

  // *** Storage API *** //
  doUpload = (fileData: any) =>
    uploadBytes(storageRef(this.storage, `${AUDIOPATH}/${fileData.name}`), fileData.file, fileData.metadata);

  deleteFile = (fileName: any) => deleteObject(storageRef(this.storage, `${AUDIOPATH}/${fileName}`));

  // *** Audio Meta Data *** //
  saveAudioMetaData = (metaData: any) =>
    setDoc(doc(this.db, AUDIOMETAPATH, metaData.audioMetaRef), metaData);

  deleteAudioMetaData = (docId: any) =>
    deleteDoc(doc(this.db, AUDIOMETAPATH, docId));

  getAudioMetaData = (docId: string) =>
    getDoc(doc(this.db, AUDIOMETAPATH, docId));

  getAudioMetaRef = () => collection(this.db, AUDIOMETAPATH);

  audioMetaList = (config: any): Query<DocumentData> => {
    let col = collection(this.db, AUDIOMETAPATH);
    const { searchFilter, sortBy } = config;
    const multiSearch = searchFilter?.multi;
    const multiActive = multiSearch?.active;
    const dateSearch = searchFilter?.date;
    const dateActive = dateSearch?.active;
    const subSearch = searchFilter?.subFilters;
    const subSearchActive = subSearch?.active;

    const useMulti =
      searchFilter &&
      multiSearch?.data &&
      Object.keys(multiSearch?.data).length > 0 &&
      multiActive;
    const useDate =
      searchFilter && Object.keys(searchFilter?.date).length > 0 && dateActive;
    const useSubSearch =
      searchFilter &&
      searchFilter?.subFilters?.filters?.length > 0 &&
      subSearchActive;
    const canFilter = useMulti || useDate || useSubSearch;

    let sortByCol = sortBy.column;

    let q: Query<DocumentData> = col;
    if (canFilter) {
      if (useMulti) {
        const useValue = !!multiSearch.useValue;
        q = query(
          q,
          where(
            useValue ? multiSearch?.useValue : multiSearch?.value,
            'array-contains-any',
            [...searchFilter.multi.data]
          )
        );
      }
      if (useDate) {
        const { from, to, type } = dateSearch;
        const oneDay = 24 * 60 * 60 * 1000;
        const useType = type === 'completed' ? 'dateCompleted' : 'dateExpires';
        sortByCol = useType;
        q = query(q, where(useType, '>=', from - oneDay), where(useType, '<=', to));
      }
      if (useSubSearch) {
        searchFilter.subFilters.filters.forEach((subFilter: any) => {
          if (subFilter.key === 'bpm') {
            const [minStr, maxStr] = subFilter.value.value.split('-');
            const min = minStr && minStr.trim() !== '' ? Number(minStr) : null;
            const max = maxStr && maxStr.trim() !== '' ? Number(maxStr) : null;
            
            if (min !== null && max !== null && !isNaN(min) && !isNaN(max)) {
              // Complete range (min-max)
              q = query(q, where('bpm', '>=', min), where('bpm', '<=', max));
            } else if (min !== null && !isNaN(min) && (maxStr === '' || maxStr === undefined)) {
              // Minimum-only range (min-)
              q = query(q, where('bpm', '>=', min));
            } else if (max !== null && !isNaN(max) && (minStr === '' || minStr === undefined)) {
              // Maximum-only range (-max)
              q = query(q, where('bpm', '<=', max));
            }
          } else if (subFilter.key === 'tags') {
            q = query(q, where('tags', 'array-contains-any', subFilter.value));
          } else {
            q = query(q, where(subFilter.key + '.label', '==', subFilter.value.label));
          }
        });
      }
    }
    q = query(q, orderBy(sortByCol, sortBy.ascending ? 'asc' : 'desc'));
    return q;
  };

  // *** Chatbot Collections *** //
  getChatbotConfig = () => getDoc(doc(this.db, 'chatbotConfig', 'gemini'));

  updateChatbotConfig = (config: any) =>
    updateDoc(doc(this.db, 'chatbotConfig', 'gemini'), config);

  getChatbotMappings = () => getDoc(doc(this.db, 'chatbotMappings', 'default'));

  updateChatbotMappings = (mappings: any) =>
    updateDoc(doc(this.db, 'chatbotMappings', 'default'), mappings);

  // *** Genres *** //
  saveGenre = (genre: any, docId: any) =>
    setDoc(doc(this.db, GENRESPATH, docId), genre);
  getGenreRef = () => doc(this.db, GENRESPATH);

  genres = () => collection(this.db, GENRESPATH);

  // *** Generic *** //
  save = (data: any, docId: any, path: any) =>
    setDoc(doc(this.db, path, docId), data);

  getRef = (path: any) => doc(this.db, path);

  collection = (path: any) => {
    return collection(this.db, path);
  };

  // Helper for modular query usage
  collectionQuery = (path: string, ...queryConstraints: any[]) => {
    return query(collection(this.db, path), ...queryConstraints);
  };

  callFunction = (name: string, data?: any) => {
    const fn = httpsCallable(this.functions, name);
    return fn(data);
  };

  // ==========================================================================
  // *** ORGANIZATION API ***
  // ==========================================================================

  // Get organization document
  getOrganization = (orgId: string) =>
    getDoc(doc(this.db, ORGANIZATIONSPATH, orgId));

  // Get all organizations (for super admin)
  getAllOrganizations = () =>
    getDocs(collection(this.db, ORGANIZATIONSPATH));

  // Create a new organization
  createOrganization = async (orgData: {
    name: string;
    type: 'artist' | 'client';
    category?: 'artist' | 'publisher' | 'agency' | 'brand' | 'production';
    createdBy: string;
    settings?: any;
  }) => {
    const orgRef = doc(collection(this.db, ORGANIZATIONSPATH));
    const now = new Date().toISOString();
    const organization: any = {
      id: orgRef.id,
      name: orgData.name,
      type: orgData.type,
      createdBy: orgData.createdBy,
      createdAt: now,
      settings: orgData.settings || {
        allowUserInvites: false,
        defaultUserRole: 'USER',
      },
    };

    // Add category if provided
    if (orgData.category) {
      organization.category = orgData.category;
    }

    await setDoc(orgRef, organization);

    // Add the creator to the members subcollection as ADMIN
    await setDoc(doc(this.db, ORGANIZATIONSPATH, orgRef.id, 'members', orgData.createdBy), {
      joinedAt: now,
      role: 'ADMIN',
      invitedBy: null,
      inviteCode: null,
      notifications: {
        licenseExpiry: true,
      },
    });

    return organization;
  };

  // Update organization settings
  updateOrganization = (orgId: string, updates: any) =>
    updateDoc(doc(this.db, ORGANIZATIONSPATH, orgId), updates);

  // Update organization name (also updates denormalized copies in ALL member user documents)
  updateOrganizationName = async (orgId: string, newName: string) => {
    try {
      // 1. Update the organization document
      await updateDoc(doc(this.db, ORGANIZATIONSPATH, orgId), { name: newName });

      // 2. Get all members of this organization
      const membersRef = collection(this.db, `${ORGANIZATIONSPATH}/${orgId}/members`);
      const membersSnapshot = await getDocs(membersRef);

      // 3. Update each member's denormalized organization name in their user document
      const updatePromises = membersSnapshot.docs.map(async (memberDoc) => {
        try {
          const memberId = memberDoc.id;
          const userRef = doc(this.db, USERSPATH, memberId);
          const userDocSnap = await getDoc(userRef);

          if (userDocSnap.exists()) {
            const userData = userDocSnap.data();
            const organizations = userData.organizations || [];
            const updatedOrgs = organizations.map((org: any) =>
              org.orgId === orgId ? { ...org, orgName: newName } : org
            );
            await updateDoc(userRef, { organizations: updatedOrgs });
          }
        } catch (memberError) {
          console.error(`Failed to update member ${memberDoc.id}:`, memberError);
        }
      });

      await Promise.all(updatePromises);
    } catch (error) {
      console.error('updateOrganizationName error:', error);
      throw error;
    }
  };

  // Delete organization (super admin only)
  deleteOrganization = (orgId: string) =>
    deleteDoc(doc(this.db, ORGANIZATIONSPATH, orgId));

  // Get user's organizations from their memberships
  getUserOrganizations = async (userId: string) => {
    const userDoc = await getDoc(doc(this.db, USERSPATH, userId));
    if (!userDoc.exists()) return [];
    const userData = userDoc.data();
    return userData.organizations || [];
  };

  // Add user to organization
  addUserToOrganization = async (
    userId: string,
    orgId: string,
    orgName: string,
    role: 'ADMIN' | 'OPERATOR' | 'USER',
    orgType?: 'artist' | 'client',
    orgCategory?: 'artist' | 'publisher' | 'agency' | 'brand' | 'production'
  ) => {
    const userRef = doc(this.db, USERSPATH, userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    const userData = userDoc.data();
    const organizations = userData.organizations || [];

    // Check if user already in org
    const existingIndex = organizations.findIndex((o: any) => o.orgId === orgId);
    if (existingIndex >= 0) {
      // Update role, type, and category
      organizations[existingIndex].role = role;
      if (orgType) {
        organizations[existingIndex].orgType = orgType;
      }
      if (orgCategory) {
        organizations[existingIndex].orgCategory = orgCategory;
      }
    } else {
      // Add new membership
      const membership: any = {
        orgId,
        orgName,
        orgType: orgType || 'artist',  // Default to 'artist' for backwards compatibility
        role,
        joinedAt: new Date().toISOString(),
      };
      if (orgCategory) {
        membership.orgCategory = orgCategory;
      }
      organizations.push(membership);
    }

    await updateDoc(userRef, { organizations });
    return organizations;
  };

  // Remove user from organization
  removeUserFromOrganization = async (userId: string, orgId: string) => {
    const userRef = doc(this.db, USERSPATH, userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    const userData = userDoc.data();
    const organizations = (userData.organizations || []).filter(
      (o: any) => o.orgId !== orgId
    );

    await updateDoc(userRef, { organizations });
    return organizations;
  };

  // Update user's current organization selection
  setUserCurrentOrg = (userId: string, orgId: string) =>
    updateDoc(doc(this.db, USERSPATH, userId), { currentOrgId: orgId });

  // ==========================================================================
  // *** ORGANIZATION INVITES API ***
  // ==========================================================================

  // Generate a unique invite code
  generateInviteCode = (): string => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Removed ambiguous chars (0,O,1,I)
    let code = '';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  // Create an invite for an organization
  createOrgInvite = async (
    orgId: string,
    orgName: string,
    createdBy: string,
    expiresInDays: number = 7,
    role: 'USER' | 'OPERATOR' | 'ADMIN' = 'USER',
    invitedEmail: string
  ) => {
    const code = this.generateInviteCode();
    const now = new Date();
    const expiresAt = new Date(now.getTime() + expiresInDays * 24 * 60 * 60 * 1000);

    const inviteData = {
      code,
      orgId,
      orgName,
      createdBy,
      createdAt: now.toISOString(),
      expiresAt: expiresAt.toISOString(),
      used: false,
      usedBy: null,
      usedAt: null,
      role,
      invitedEmail: invitedEmail.toLowerCase(),
    };

    // Store in global invites collection (for lookup by code)
    await setDoc(doc(this.db, 'invites', code), inviteData);

    // Also store in org's invites subcollection (for org admin to manage)
    await setDoc(doc(this.db, ORGANIZATIONSPATH, orgId, 'invites', code), inviteData);

    return inviteData;
  };

  // Get invite by code (for validation)
  getInviteByCode = async (code: string): Promise<InviteData | null> => {
    const inviteDoc = await getDoc(doc(this.db, 'invites', code));
    if (!inviteDoc.exists()) {
      return null;
    }
    return { id: inviteDoc.id, ...inviteDoc.data() } as InviteData;
  };

  // Validate an invite code (optionally check email match)
  validateInvite = async (code: string, userEmail?: string) => {
    const invite = await this.getInviteByCode(code);

    if (!invite) {
      return { valid: false, error: 'Invite not found' };
    }

    if (invite.used) {
      return { valid: false, error: 'Invite has already been used' };
    }

    const now = new Date();
    const expiresAt = new Date(invite.expiresAt);
    if (now > expiresAt) {
      return { valid: false, error: 'Invite has expired' };
    }

    // If user email provided, check if it matches the invited email
    if (userEmail && invite.invitedEmail) {
      if (userEmail.toLowerCase() !== invite.invitedEmail.toLowerCase()) {
        return {
          valid: false,
          error: 'This invite was sent to a different email address',
          invitedEmail: invite.invitedEmail,
          invite
        };
      }
    }

    return { valid: true, invite };
  };

  // Use an invite (mark as used and add user to org)
  useInvite = async (code: string, userId: string, userEmail: string) => {
    // Validate with email check
    const validation = await this.validateInvite(code, userEmail);

    if (!validation.valid) {
      throw new Error(validation.error);
    }

    const invite = validation.invite!;
    const now = new Date().toISOString();

    // Mark invite as used in both locations
    const updateData = {
      used: true,
      usedBy: userId,
      usedAt: now,
    };

    await updateDoc(doc(this.db, 'invites', code), updateData);
    await updateDoc(doc(this.db, ORGANIZATIONSPATH, invite.orgId, 'invites', code), updateData);

    // Fetch org type and category from the organization document
    const orgDoc = await getDoc(doc(this.db, ORGANIZATIONSPATH, invite.orgId));
    const orgData = orgDoc.exists() ? orgDoc.data() : null;
    const orgType = orgData?.type || 'artist';
    const orgCategory = orgData?.category || null;

    // Add user to the organization
    await this.addUserToOrganization(userId, invite.orgId, invite.orgName, invite.role, orgType, orgCategory);

    // Add user to org's members subcollection (for admin to view)
    await setDoc(doc(this.db, ORGANIZATIONSPATH, invite.orgId, 'members', userId), {
      joinedAt: now,
      role: invite.role,
      invitedBy: invite.createdBy,
      inviteCode: code,
      notifications: {
        licenseExpiry: true,
      },
    });

    return { ...invite, orgType, orgCategory };
  };

  // Get all invites for an organization (for admin)
  getOrgInvites = async (orgId: string) => {
    const invitesRef = collection(this.db, ORGANIZATIONSPATH, orgId, 'invites');
    const snapshot = await getDocs(invitesRef);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  };

  // Delete/revoke an invite
  deleteInvite = async (orgId: string, code: string) => {
    await deleteDoc(doc(this.db, 'invites', code));
    await deleteDoc(doc(this.db, ORGANIZATIONSPATH, orgId, 'invites', code));
  };

  // Get all members of an organization (for admin)
  getOrgMembers = async (orgId: string) => {
    const membersRef = collection(this.db, ORGANIZATIONSPATH, orgId, 'members');
    const snapshot = await getDocs(membersRef);

    // Fetch user details for each member
    const membersWithDetails = await Promise.all(
      snapshot.docs.map(async (memberDoc) => {
        const memberData = memberDoc.data();
        const userId = memberDoc.id;

        // Try to get user details from users collection
        let username = null;
        let email = null;
        try {
          const userDoc = await getDoc(doc(this.db, USERSPATH, userId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            username = userData.username || null;
            email = userData.email || null;
          }
        } catch (err) {
          // User document might not exist or be inaccessible
          console.error(`Failed to fetch user details for ${userId}:`, err);
        }

        return {
          id: userId,
          username,
          email,
          ...memberData,
        };
      })
    );

    return membersWithDetails;
  };

  // Update a member's role in an organization
  updateMemberRole = async (orgId: string, userId: string, newRole: 'ADMIN' | 'OPERATOR' | 'USER') => {
    // Update in org's members subcollection
    await updateDoc(doc(this.db, ORGANIZATIONSPATH, orgId, 'members', userId), {
      role: newRole,
    });

    // Update in user's organizations array
    const userRef = doc(this.db, USERSPATH, userId);
    const userDoc = await getDoc(userRef);
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const organizations = (userData.organizations || []).map((o: any) =>
        o.orgId === orgId ? { ...o, role: newRole } : o
      );
      await updateDoc(userRef, { organizations });
    }
  };

  // Get notification preferences for the current user in an org
  getMemberNotificationPreferences = async (orgId: string, userId: string) => {
    const memberRef = doc(this.db, ORGANIZATIONSPATH, orgId, 'members', userId);
    const memberDoc = await getDoc(memberRef);
    if (memberDoc.exists()) {
      const data = memberDoc.data();
      // Default all notifications to true if not set
      return data.notifications || { licenseExpiry: true };
    }
    return { licenseExpiry: true };
  };

  // Update notification preferences for the current user in an org
  updateMemberNotificationPreferences = async (orgId: string, userId: string, notifications: Record<string, boolean>) => {
    const memberRef = doc(this.db, ORGANIZATIONSPATH, orgId, 'members', userId);
    await setDoc(memberRef, { notifications }, { merge: true });
  };

  // Get display preferences for the current user in an org
  getMemberDisplayPreferences = async (orgId: string, userId: string) => {
    const memberRef = doc(this.db, ORGANIZATIONSPATH, orgId, 'members', userId);
    const memberDoc = await getDoc(memberRef);
    if (memberDoc.exists()) {
      const data = memberDoc.data();
      return data.displayPreferences || { hideMnemonics: true, hideLicensed: true };
    }
    return { hideMnemonics: true, hideLicensed: true };
  };

  // Update display preferences for the current user in an org
  updateMemberDisplayPreferences = async (orgId: string, userId: string, displayPreferences: Record<string, boolean>) => {
    const memberRef = doc(this.db, ORGANIZATIONSPATH, orgId, 'members', userId);
    await setDoc(memberRef, { displayPreferences }, { merge: true });
  };

  // Remove a member from an organization
  removeMember = async (orgId: string, userId: string) => {
    // Remove from org's members subcollection
    await deleteDoc(doc(this.db, ORGANIZATIONSPATH, orgId, 'members', userId));

    // Remove from user's organizations array
    try {
      await this.removeUserFromOrganization(userId, orgId);
    } catch (err) {
      console.error('Failed to remove org from user document:', err);
      // Re-throw so the UI knows it failed
      throw err;
    }
  };

  // Soft delete an organization
  // - Marks org as deleted (doesn't remove data)
  // - Removes org from all members' organizations array
  // - Unlinks global entries (artists-global, client, agency) and stores their IDs for restore
  softDeleteOrganization = async (orgId: string, deletedBy: string) => {
    // Get all members first
    const membersRef = collection(this.db, ORGANIZATIONSPATH, orgId, 'members');
    const membersSnapshot = await getDocs(membersRef);

    // Remove org from each member's organizations array
    const removePromises = membersSnapshot.docs.map(async (memberDoc) => {
      const userId = memberDoc.id;
      try {
        await this.removeUserFromOrganization(userId, orgId);
      } catch (err) {
        console.error(`Failed to remove org from user ${userId}:`, err);
        // Continue with other members even if one fails
      }
    });

    await Promise.all(removePromises);

    // Find and unlink global entries that reference this org
    const linkedEntries: { collection: string; docId: string }[] = [];
    const globalCollections = [GLOBAL_ARTISTPATH, CLIENTPATH, AGENCYPATH];

    for (const collectionPath of globalCollections) {
      try {
        const collectionRef = collection(this.db, collectionPath);
        const q = query(collectionRef, where('linkedOrgId', '==', orgId));
        const snapshot = await getDocs(q);

        for (const docSnap of snapshot.docs) {
          // Store the linked entry info for potential restore
          linkedEntries.push({ collection: collectionPath, docId: docSnap.id });

          // Remove the link from the global entry
          await updateDoc(doc(this.db, collectionPath, docSnap.id), {
            linkedOrgId: deleteField(),
            linkedAt: deleteField(),
          });
        }
      } catch (err) {
        console.error(`Failed to unlink global entries in ${collectionPath}:`, err);
        // Continue with other collections
      }
    }

    // Mark the organization as deleted (soft delete)
    // Store linked entries so they can be restored later
    const orgRef = doc(this.db, ORGANIZATIONSPATH, orgId);
    await updateDoc(orgRef, {
      deletedAt: new Date().toISOString(),
      deletedBy: deletedBy,
      isDeleted: true,
      _linkedEntries: linkedEntries.length > 0 ? linkedEntries : deleteField(),
    });

    return true;
  };

  // Restore a soft-deleted organization
  // Re-adds the org to all members' organizations arrays, removes deleted flags,
  // and re-links global entries that were unlinked during soft delete
  restoreOrganization = async (orgId: string) => {
    // Get the organization document
    const orgRef = doc(this.db, ORGANIZATIONSPATH, orgId);
    const orgDoc = await getDoc(orgRef);

    if (!orgDoc.exists()) {
      throw new Error('Organization not found');
    }

    const orgData = orgDoc.data();
    const orgName = orgData.name;
    const orgType = orgData.type;
    const orgCategory = orgData.category;

    // Get all members from the members subcollection
    const membersRef = collection(this.db, ORGANIZATIONSPATH, orgId, 'members');
    const membersSnapshot = await getDocs(membersRef);

    // Re-add org to each member's organizations array
    const restorePromises = membersSnapshot.docs.map(async (memberDoc) => {
      const userId = memberDoc.id;
      const memberData = memberDoc.data();
      const role = memberData.role || 'USER';

      try {
        await this.addUserToOrganization(userId, orgId, orgName, role, orgType, orgCategory);
      } catch (err) {
        console.error(`Failed to restore org for user ${userId}:`, err);
        // Continue with other members even if one fails
      }
    });

    await Promise.all(restorePromises);

    // Restore global entry links if they were stored during soft delete
    const linkedEntries = orgData._linkedEntries as { collection: string; docId: string }[] | undefined;
    if (linkedEntries && linkedEntries.length > 0) {
      for (const entry of linkedEntries) {
        try {
          await updateDoc(doc(this.db, entry.collection, entry.docId), {
            linkedOrgId: orgId,
            linkedAt: Date.now(),
          });
        } catch (err) {
          console.error(`Failed to restore link for ${entry.collection}/${entry.docId}:`, err);
          // Continue with other entries
        }
      }
    }

    // Remove the deleted flags and linked entries backup
    await updateDoc(orgRef, {
      deletedAt: deleteField(),
      deletedBy: deleteField(),
      isDeleted: deleteField(),
      _linkedEntries: deleteField(),
    });

    return true;
  };

  // Check for organizations pending restoration and restore them
  // Triggered on user login - checks if any org has isDeleted = false
  // (meaning admin changed it from true to false to trigger restore)
  // and the user is in that org's members subcollection
  checkAndRestorePendingOrgs = async (userId: string) => {
    try {
      // Query all orgs with isDeleted = false (restore trigger)
      const orgsRef = collection(this.db, ORGANIZATIONSPATH);
      const restoreQuery = query(orgsRef, where('isDeleted', '==', false));
      const snapshot = await getDocs(restoreQuery);

      for (const orgDoc of snapshot.docs) {
        const orgId = orgDoc.id;

        // Check if this user is in the members subcollection
        const memberRef = doc(this.db, ORGANIZATIONSPATH, orgId, 'members', userId);
        const memberDoc = await getDoc(memberRef);

        if (memberDoc.exists()) {
          // User is a member - restore the org
          await this.restoreOrganization(orgId);
        }
      }
    } catch (err) {
      console.error('Error checking for pending org restorations:', err);
      // Don't throw - this shouldn't block login
    }
  };

  // ==========================================================================
  // *** ORGANIZATION-SCOPED AUDIO META API ***
  // ==========================================================================

  // Get org-scoped audio meta reference
  getOrgAudioMetaRef = (orgId: string) =>
    collection(this.db, getOrgCollectionPath(orgId, AUDIOMETAPATH));

  // Save audio meta data to organization
  saveOrgAudioMetaData = (orgId: string, metaData: any) =>
    setDoc(
      doc(this.db, getOrgCollectionPath(orgId, AUDIOMETAPATH), metaData.audioMetaRef),
      metaData
    );

  // Get audio meta data from organization
  getOrgAudioMetaData = (orgId: string, docId: string) =>
    getDoc(doc(this.db, getOrgCollectionPath(orgId, AUDIOMETAPATH), docId));

  // Delete audio meta data from organization
  deleteOrgAudioMetaData = (orgId: string, docId: string) =>
    deleteDoc(doc(this.db, getOrgCollectionPath(orgId, AUDIOMETAPATH), docId));

  // Org-scoped audio meta list query
  orgAudioMetaList = (orgId: string, config: any): Query<DocumentData> => {
    const orgPath = getOrgCollectionPath(orgId, AUDIOMETAPATH);
    let col = collection(this.db, orgPath);
    const { searchFilter, sortBy } = config;
    const multiSearch = searchFilter?.multi;
    const multiActive = multiSearch?.active;
    const dateSearch = searchFilter?.date;
    const dateActive = dateSearch?.active;
    const subSearch = searchFilter?.subFilters;
    const subSearchActive = subSearch?.active;

    const useMulti =
      searchFilter &&
      multiSearch?.data &&
      Object.keys(multiSearch?.data).length > 0 &&
      multiActive;
    const useDate =
      searchFilter && Object.keys(searchFilter?.date).length > 0 && dateActive;
    const useSubSearch =
      searchFilter &&
      searchFilter?.subFilters?.filters?.length > 0 &&
      subSearchActive;
    const canFilter = useMulti || useDate || useSubSearch;

    let sortByCol = sortBy.column;

    let q: Query<DocumentData> = col;
    if (canFilter) {
      if (useMulti) {
        const useValue = !!multiSearch.useValue;
        q = query(
          q,
          where(
            useValue ? multiSearch?.useValue : multiSearch?.value,
            'array-contains-any',
            [...searchFilter.multi.data]
          )
        );
      }
      if (useDate) {
        const { from, to, type } = dateSearch;
        const oneDay = 24 * 60 * 60 * 1000;
        const useType = type === 'completed' ? 'dateCompleted' : 'dateExpires';
        sortByCol = useType;
        q = query(q, where(useType, '>=', from - oneDay), where(useType, '<=', to));
      }
      if (useSubSearch) {
        searchFilter.subFilters.filters.forEach((subFilter: any) => {
          if (subFilter.key === 'bpm') {
            const [minStr, maxStr] = subFilter.value.value.split('-');
            const min = minStr && minStr.trim() !== '' ? Number(minStr) : null;
            const max = maxStr && maxStr.trim() !== '' ? Number(maxStr) : null;

            if (min !== null && max !== null && !isNaN(min) && !isNaN(max)) {
              q = query(q, where('bpm', '>=', min), where('bpm', '<=', max));
            } else if (min !== null && !isNaN(min) && (maxStr === '' || maxStr === undefined)) {
              q = query(q, where('bpm', '>=', min));
            } else if (max !== null && !isNaN(max) && (minStr === '' || minStr === undefined)) {
              q = query(q, where('bpm', '<=', max));
            }
          } else if (subFilter.key === 'tags') {
            q = query(q, where('tags', 'array-contains-any', subFilter.value));
          } else {
            q = query(q, where(subFilter.key + '.label', '==', subFilter.value.label));
          }
        });
      }
    }
    q = query(q, orderBy(sortByCol, sortBy.ascending ? 'asc' : 'desc'));
    return q;
  };

  // ==========================================================================
  // *** ORGANIZATION-SCOPED STORAGE API ***
  // ==========================================================================

  // Get org-scoped audio storage reference
  getOrgAudioRef = (orgId: string) =>
    storageRef(this.storage, `${AUDIOPATH}${orgId}`);

  // Upload file to org-scoped storage
  doOrgUpload = (orgId: string, trackId: string, fileData: any) => {
    const path = getOrgStoragePath(orgId, trackId);
    return uploadBytes(
      storageRef(this.storage, `${path}${fileData.name}`),
      fileData.file,
      fileData.metadata
    );
  };

  // Delete file from org-scoped storage
  deleteOrgFile = (orgId: string, trackId: string, fileName: string) => {
    const path = getOrgStoragePath(orgId, trackId);
    return deleteObject(storageRef(this.storage, `${path}${fileName}`));
  };

  // List files in org-scoped track folder
  listOrgTrackFiles = (orgId: string, trackId: string) => {
    const path = getOrgStoragePath(orgId, trackId);
    return listAll(storageRef(this.storage, path));
  };

  // ==========================================================================
  // *** ORGANIZATION-SCOPED GENERIC COLLECTION API ***
  // ==========================================================================

  // Get org-scoped collection reference
  getOrgCollection = (orgId: string, collectionName: string) =>
    collection(this.db, getOrgCollectionPath(orgId, collectionName));

  // Save to org-scoped collection
  saveToOrgCollection = (orgId: string, collectionName: string, docId: string, data: any) =>
    setDoc(doc(this.db, getOrgCollectionPath(orgId, collectionName), docId), data);

  // Get from org-scoped collection
  getFromOrgCollection = (orgId: string, collectionName: string, docId: string) =>
    getDoc(doc(this.db, getOrgCollectionPath(orgId, collectionName), docId));

  // Delete from org-scoped collection
  deleteFromOrgCollection = (orgId: string, collectionName: string, docId: string) =>
    deleteDoc(doc(this.db, getOrgCollectionPath(orgId, collectionName), docId));

  // Query org-scoped collection
  queryOrgCollection = (orgId: string, collectionName: string, ...queryConstraints: any[]) =>
    query(collection(this.db, getOrgCollectionPath(orgId, collectionName)), ...queryConstraints);

  // ==========================================================================
  // *** LICENSE SUBCOLLECTION API ***
  // ==========================================================================

  // Get licenses collection reference for a track
  getLicensesCollection = (orgId: string, trackId: string) =>
    collection(this.db, getOrgCollectionPath(orgId, AUDIOMETAPATH), trackId, 'licenses');

  // Create a new license document
  createLicense = async (
    orgId: string,
    trackId: string,
    licenseData: any
  ): Promise<string> => {
    const licensesRef = this.getLicensesCollection(orgId, trackId);
    const newLicenseRef = doc(licensesRef);

    const fullLicenseData = {
      ...licenseData,
      id: newLicenseRef.id,
      sourceOrgId: orgId,
      createdAt: Date.now(),
    };

    await setDoc(newLicenseRef, fullLicenseData);

    // Note: For direct shares to existing orgs, we no longer create audiometa immediately.
    // The license remains 'pending' and audiometa is created when the client claims it.
    // This gives clients explicit control over what enters their library.

    // Update the source audiometa doc's licenseStatus so the license filter works
    const trackRef = doc(this.db, getOrgCollectionPath(orgId, AUDIOMETAPATH), trackId);
    const isBlanket = !licenseData.dateExpires;
    await updateDoc(trackRef, {
      dateExpires: isBlanket ? null : licenseData.dateExpires,
      licenseStatus: {
        isLicensed: true,
        durationType: licenseData.licenseType || (isBlanket ? 'blanket' : 'custom'),
        startDate: licenseData.dateStart || null,
        expiryDate: isBlanket ? null : licenseData.dateExpires,
      },
    });

    // If this is an exclusive license, unpublish the track from public catalogue
    if (licenseData.isExclusive) {
      // Check if track is currently public
      const publicDoc = await this.getPublicCatalogueTrack(trackId);
      if (publicDoc) {
        // Unpublish - this deletes the publicCatalogue doc and sets isPublic: false on audiometa
        await this.unpublishFromPublicCatalogue(orgId, trackId);
        console.log('Track unpublished from public catalogue due to exclusive license');
      }
    }

    return newLicenseRef.id;
  };

  /**
   * Create an audiometa document in the client org from license data
   * Used for both direct sharing and claim flow
   */
  private createClientAudiometaFromLicense = async (
    sourceOrgId: string,
    trackId: string,
    licenseId: string,
    clientOrgId: string,
    licenseData: any,
    additionalFields?: Record<string, any>
  ): Promise<void> => {
    const clientTrackRef = doc(
      this.db,
      getOrgCollectionPath(clientOrgId, AUDIOMETAPATH),
      trackId
    );

    const licensePath = `${getOrgCollectionPath(sourceOrgId, AUDIOMETAPATH)}/${trackId}/licenses/${licenseId}`;

    const clientTrackData = {
      // Track metadata from license snapshot
      trackName: licenseData.trackName || 'Unknown Track',
      key: licenseData.key || null,
      bpm: licenseData.bpm || null,
      genre: licenseData.genre || null,
      tags: licenseData.tags || [],
      composer: licenseData.composer || [],
      artist: licenseData.artist || null,
      album: licenseData.album || null,
      recordLabel: licenseData.recordLabel || null,
      publisher: licenseData.publisher || null,
      sessionArtists: licenseData.sessionArtists || [],
      trackColor: licenseData.trackColor || null,

      // Audio files for playback
      mp3Files: licenseData.mp3Files || [],
      wavFiles: licenseData.wavFiles || [],
      defaultMp3Id: licenseData.defaultMp3Id || null,

      // License-specific metadata
      jobTitle: licenseData.jobTitle || null,
      client: licenseData.client || null,
      agency: licenseData.agency || null,
      producer: licenseData.producer || null,
      creativedirector: licenseData.creativeDirectors || [],
      flightingPlatform: licenseData.flightingPlatform || [],
      territories: licenseData.territories || [],
      dateExpires: licenseData.dateExpires || null,
      dateStart: licenseData.dateStart || null,

      // License reference fields
      isLicensedTrack: true,
      sourceOrgId: sourceOrgId,
      sourceTrackId: trackId,
      licenseId: licenseId,
      licensePath: licensePath,
      sourceOrgName: licenseData.sourceOrgName || 'Unknown Organization',

      // Timestamps
      createdAt: Date.now(),

      // Additional fields (e.g., claimedAt, claimedBy for claim flow)
      ...additionalFields,
    };

    await setDoc(clientTrackRef, clientTrackData);
  };

  // Get a specific license document
  getLicense = async (orgId: string, trackId: string, licenseId: string) => {
    const licenseRef = doc(this.db, getOrgCollectionPath(orgId, AUDIOMETAPATH), trackId, 'licenses', licenseId);
    const docSnap = await getDoc(licenseRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  };

  // Get all licenses for a track
  getLicensesForTrack = async (orgId: string, trackId: string) => {
    const licensesRef = this.getLicensesCollection(orgId, trackId);
    const q = query(licensesRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  };

  // Get all licenses for a track that belong to a specific client
  // Used by clients to see their license history for a track
  getLicensesForClient = async (sourceOrgId: string, trackId: string, clientOrgId: string) => {
    const licensesRef = this.getLicensesCollection(sourceOrgId, trackId);
    const q = query(
      licensesRef,
      where('clientOrgId', '==', clientOrgId),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  };

  // Real-time listener for all licenses for a track (for artist/owner view)
  subscribeLicensesForTrack = (
    orgId: string,
    trackId: string,
    callback: (licenses: any[]) => void
  ) => {
    const licensesRef = this.getLicensesCollection(orgId, trackId);
    const q = query(licensesRef, orderBy('createdAt', 'desc'));
    return onSnapshot(q, (snapshot) => {
      const licenses = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      callback(licenses);
    });
  };

  // Real-time listener for licenses belonging to a specific client
  subscribeLicensesForClient = (
    sourceOrgId: string,
    trackId: string,
    clientOrgId: string,
    callback: (licenses: any[]) => void
  ) => {
    const licensesRef = this.getLicensesCollection(sourceOrgId, trackId);
    const q = query(
      licensesRef,
      where('clientOrgId', '==', clientOrgId),
      orderBy('createdAt', 'desc')
    );
    return onSnapshot(q, (snapshot) => {
      const licenses = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      callback(licenses);
    });
  };

  // Update a license document
  updateLicense = async (
    orgId: string,
    trackId: string,
    licenseId: string,
    updates: any
  ) => {
    const licenseRef = doc(this.db, getOrgCollectionPath(orgId, AUDIOMETAPATH), trackId, 'licenses', licenseId);
    await updateDoc(licenseRef, updates);
  };

  // Delete a license document
  deleteLicense = async (orgId: string, trackId: string, licenseId: string) => {
    const licenseRef = doc(this.db, getOrgCollectionPath(orgId, AUDIOMETAPATH), trackId, 'licenses', licenseId);
    await deleteDoc(licenseRef);
  };

  // Deactivate all licenses for a track (set status to 'inactive')
  deactivateTrackLicenses = async (orgId: string, trackId: string) => {
    const licenses = await this.getLicensesForTrack(orgId, trackId);
    const updatePromises = licenses.map(license =>
      this.updateLicense(orgId, trackId, license.id, {
        status: 'inactive',
        deactivatedAt: Date.now(),
        deactivatedBy: this.auth.currentUser?.uid || null,
      })
    );
    await Promise.all(updatePromises);

    // Update track's license status since all licenses are now inactive
    const trackRef = doc(this.db, getOrgCollectionPath(orgId, AUDIOMETAPATH), trackId);
    await updateDoc(trackRef, {
      dateExpires: null,
      licenseStatus: {
        isLicensed: false,
        durationType: 'none',
        startDate: null,
        expiryDate: null,
      },
    });

    return licenses.length;
  };

  // Deactivate a single license and update track's dateExpires to greatest remaining active license
  deactivateSingleLicense = async (orgId: string, trackId: string, licenseId: string) => {
    // First, get the license data to check if it was exclusive
    const licenseData = await this.getLicense(orgId, trackId, licenseId) as any;
    const wasExclusive = licenseData?.isExclusive === true;

    // Deactivate the specific license
    await this.updateLicense(orgId, trackId, licenseId, {
      status: 'inactive',
      deactivatedAt: Date.now(),
      deactivatedBy: this.auth.currentUser?.uid || null,
    });

    // If this was an exclusive license, unhide the track and create notification
    if (wasExclusive) {
      // Check if there are any other active exclusive licenses
      const activeExclusive = await this.getActiveExclusiveLicense(orgId, trackId);
      if (!activeExclusive) {
        // No active exclusive licenses - create notification for artist to republish
        await this.createExclusiveLicenseEndedNotification(orgId, trackId, licenseData);
        console.log('Exclusive license ended - notification created for republishing');
      }
    }

    // Get all licenses for the track to find remaining active ones
    const allLicenses = await this.getLicensesForTrack(orgId, trackId);

    // Filter to only active licenses (excluding the one we just deactivated)
    const activeLicenses = allLicenses.filter((license: any) => {
      if (license.id === licenseId) return false;
      if (license.status === 'inactive') return false;
      // Check if expired
      if (license.dateExpires && license.dateExpires < Date.now()) return false;
      return true;
    });

    const trackRef = doc(this.db, getOrgCollectionPath(orgId, AUDIOMETAPATH), trackId);

    if (activeLicenses.length === 0) {
      // No active licenses remaining - clear track's license status
      await updateDoc(trackRef, {
        dateExpires: null,
        licenseStatus: {
          isLicensed: false,
          durationType: 'none',
          startDate: null,
          expiryDate: null,
        },
      });
    } else {
      // Find the greatest dateExpires among active licenses
      // null dateExpires means blanket license (perpetual)
      let maxDateExpires: number | null = null;
      let hasBlanket = false;

      for (const license of activeLicenses as any[]) {
        if (license.dateExpires === null) {
          hasBlanket = true;
        } else if (maxDateExpires === null || license.dateExpires > maxDateExpires) {
          maxDateExpires = license.dateExpires;
        }
      }

      // If there's a blanket license, dateExpires should be null (perpetual)
      // Otherwise use the greatest dateExpires
      const newDateExpires = hasBlanket ? null : maxDateExpires;

      // Find the most recent active license for licenseType
      const mostRecentActive = (activeLicenses as any[]).sort((a, b) => b.createdAt - a.createdAt)[0];

      await updateDoc(trackRef, {
        dateExpires: newDateExpires,
        licenseStatus: {
          isLicensed: true,
          durationType: mostRecentActive.licenseType || 'custom',
          startDate: mostRecentActive.dateStart || null,
          expiryDate: newDateExpires,
        },
      });
    }
  };

  // Share a license (update with clientOrgId or generate claim code)
  shareLicense = async (
    orgId: string,
    trackId: string,
    licenseId: string,
    shareData: { clientOrgId?: string | null; claimCode?: string }
  ) => {
    const licenseRef = doc(this.db, getOrgCollectionPath(orgId, AUDIOMETAPATH), trackId, 'licenses', licenseId);
    const updates: any = {
      sharedAt: Date.now(),
      sharedBy: this.auth.currentUser?.uid || null,
    };

    if (shareData.clientOrgId) {
      updates.clientOrgId = shareData.clientOrgId;
      updates.status = 'active';
    } else if (shareData.claimCode) {
      updates.claimCode = shareData.claimCode;
      updates.status = 'pending';
    }

    await updateDoc(licenseRef, updates);
  };

  /**
   * Get license by claim code (for claim flow)
   * Uses collectionGroup query to search across all organizations' licenses
   * Returns license data with embedded snapshot fields for display
   * Note: License doc contains trackName, sourceOrgName snapshots to avoid auth-required reads
   */
  getLicenseByClaimCode = async (claimCode: string): Promise<{
    license: any;
    track: any;
    sourceOrg: any;
    trackId: string;
    orgId: string;
    licenseId: string;
  } | null> => {
    try {
      // Query all licenses subcollections for matching claim code
      const licensesQuery = query(
        collectionGroup(this.db, 'licenses'),
        where('claimCode', '==', claimCode)
      );

      const querySnapshot = await getDocs(licensesQuery);

      if (querySnapshot.empty) {
        return null;
      }

      // Get the first matching license
      const licenseDoc = querySnapshot.docs[0];
      const licenseData = { id: licenseDoc.id, ...licenseDoc.data() } as any;

      // Parse the path to get orgId and trackId
      // Path format: organizations/{orgId}/audiometa/{trackId}/licenses/{licenseId}
      const pathSegments = licenseDoc.ref.path.split('/');
      const orgId = pathSegments[1];
      const trackId = pathSegments[3];

      // Use snapshot fields from license doc (avoids needing authenticated reads)
      // These are stored when the license is created
      const trackData = {
        id: licenseData.trackId || trackId,
        trackName: licenseData.trackName || 'Unknown Track',
      };

      const orgData = {
        id: orgId,
        name: licenseData.sourceOrgName || 'Unknown Organization',
      };

      return {
        license: licenseData,
        track: trackData,
        sourceOrg: orgData,
        trackId,
        orgId,
        licenseId: licenseDoc.id,
      };
    } catch (error) {
      console.error('Error fetching license by claim code:', error);
      return null;
    }
  };

  /**
   * Claim a license - update clientOrgId and mark as active
   * Called when a user claims a license with their organization
   * Also links the claiming org to any global client/agency entries on the license
   */
  claimLicense = async (
    sourceOrgId: string,
    trackId: string,
    licenseId: string,
    claimingOrgId: string,
    claimingUserId: string,
    claimingOrgName?: string
  ): Promise<boolean> => {
    try {
      const licenseRef = doc(
        this.db,
        getOrgCollectionPath(sourceOrgId, AUDIOMETAPATH),
        trackId,
        'licenses',
        licenseId
      );

      // Get the license data first to check for client/agency fields
      const licenseSnap = await getDoc(licenseRef);
      const licenseData = licenseSnap.exists() ? licenseSnap.data() : null;

      if (!licenseData) {
        console.error('License not found');
        return false;
      }

      // Update the license with claiming org info
      await updateDoc(licenseRef, {
        clientOrgId: claimingOrgId,
        clientOrgName: claimingOrgName || null,
        claimCode: null, // Clear the claim code once claimed
        status: 'active',
        claimedAt: Date.now(),
        claimedBy: claimingUserId,
      });

      // Create audiometa document in the client org's collection
      await this.createClientAudiometaFromLicense(
        sourceOrgId,
        trackId,
        licenseId,
        claimingOrgId,
        licenseData,
        {
          claimedAt: Date.now(),
          claimedBy: claimingUserId,
        }
      );

      // Link the claiming org to global client/agency entries if they exist and aren't already linked
      await this.linkOrgToGlobalEntries(licenseData, claimingOrgId);

      // Notify the artist org (in-app + email). Share-link path, so wasShareLink=true.
      // Best-effort — a notification failure shouldn't fail the claim.
      try {
        const notify = httpsCallable(this.functionsWest1, 'notifyLicenseClaimed');
        await notify({ sourceOrgId, trackId, licenseId, wasShareLink: true });
      } catch (notifyErr) {
        console.error('notifyLicenseClaimed failed (share-link claim):', notifyErr);
      }

      return true;
    } catch (error) {
      console.error('Error claiming license:', error);
      return false;
    }
  };

  /**
   * Link an organization to global client/agency entries
   * Only updates entries that don't already have a linkedOrgId
   */
  private linkOrgToGlobalEntries = async (
    licenseData: any,
    orgId: string
  ): Promise<void> => {
    try {
      const updates: Promise<void>[] = [];

      // Check client field
      if (licenseData.client?.value) {
        updates.push(this.linkOrgToGlobalEntry(CLIENTPATH, licenseData.client.value, orgId));
      }

      // Check agency field
      if (licenseData.agency?.value) {
        updates.push(this.linkOrgToGlobalEntry(AGENCYPATH, licenseData.agency.value, orgId));
      }

      await Promise.all(updates);
    } catch (error) {
      // Log but don't fail the claim if linking fails
      console.error('Error linking org to global entries:', error);
    }
  };

  /**
   * Link an organization to a specific global entry (client or agency)
   * Only updates if the entry doesn't already have a linkedOrgId
   */
  private linkOrgToGlobalEntry = async (
    collectionPath: string,
    entryId: string,
    orgId: string
  ): Promise<void> => {
    try {
      const entryRef = doc(this.db, collectionPath, entryId);
      const entrySnap = await getDoc(entryRef);

      if (entrySnap.exists()) {
        const entryData = entrySnap.data();
        // Only link if not already linked to an org
        if (!entryData.linkedOrgId) {
          await updateDoc(entryRef, {
            linkedOrgId: orgId,
            linkedAt: Date.now(),
          });
          console.log(`Linked org ${orgId} to ${collectionPath}/${entryId}`);
        }
      }
    } catch (error) {
      console.error(`Error linking org to ${collectionPath}/${entryId}:`, error);
    }
  };

  /**
   * Validate a claim code
   * Returns validation result and license info if valid
   */
  validateClaimCode = async (claimCode: string): Promise<{
    valid: boolean;
    error?: string;
    licenseInfo?: {
      license: any;
      track: any;
      sourceOrg: any;
      trackId: string;
      orgId: string;
      licenseId: string;
    };
  }> => {
    try {
      const result = await this.getLicenseByClaimCode(claimCode);

      if (!result) {
        return { valid: false, error: 'Invalid claim code' };
      }

      const { license } = result;

      // Check if already claimed
      if (license.clientOrgId) {
        return { valid: false, error: 'This license has already been claimed' };
      }

      // Check if license is expired
      if (license.dateExpires && license.dateExpires < Date.now()) {
        return { valid: false, error: 'This license has expired' };
      }

      // Check if license status is not pending (should be pending for claim)
      if (license.status !== 'pending') {
        return { valid: false, error: 'This license is not available for claiming' };
      }

      return { valid: true, licenseInfo: result };
    } catch (error) {
      console.error('Error validating claim code:', error);
      return { valid: false, error: 'Error validating claim code' };
    }
  };

  // Update track's dateExpires field (for backwards compatibility with existing license status)
  updateTrackLicenseStatus = async (
    orgId: string,
    trackId: string,
    updates: { dateExpires?: number | null; licenseStatus?: any }
  ) => {
    const trackRef = doc(this.db, getOrgCollectionPath(orgId, AUDIOMETAPATH), trackId);
    await updateDoc(trackRef, updates);
  };

  /**
   * Get all tracks licensed TO an organization (where they are the client)
   * Used to display licensed tracks in the client org's library
   * Returns track-like objects with license info embedded
   */
  getLicensedTracksForOrg = async (clientOrgId: string): Promise<any[]> => {
    try {
      // Query all licenses where this org is the client
      const licensesQuery = query(
        collectionGroup(this.db, 'licenses'),
        where('clientOrgId', '==', clientOrgId),
        where('status', '==', 'active')
      );

      const querySnapshot = await getDocs(licensesQuery);

      if (querySnapshot.empty) {
        return [];
      }

      // Transform licenses into track-like objects for the UI
      const licensedTracks = querySnapshot.docs.map((licenseDoc) => {
        const licenseData = licenseDoc.data();

        // Parse the path to get sourceOrgId and trackId
        // Path format: organizations/{orgId}/audiometa/{trackId}/licenses/{licenseId}
        const pathSegments = licenseDoc.ref.path.split('/');
        const sourceOrgId = pathSegments[1];
        const trackId = pathSegments[3];

        // Return a track-like object with license info
        return {
          // Use the original trackId so audio playback works
          id: trackId,
          trackId: trackId,

          // Track display info from license snapshot
          trackName: licenseData.trackName || 'Unknown Track',

          // License metadata
          licenseId: licenseDoc.id,
          licenseType: licenseData.licenseType,
          dateExpires: licenseData.dateExpires,
          dateStart: licenseData.dateStart,
          jobTitle: licenseData.jobTitle,
          client: licenseData.client,
          agency: licenseData.agency,
          producer: licenseData.producer,
          creativeDirectors: licenseData.creativeDirectors,
          flightingPlatform: licenseData.flightingPlatform || [],
          territories: licenseData.territories,
          notes: licenseData.notes,

          // Source info
          sourceOrgId: sourceOrgId,
          sourceOrgName: licenseData.sourceOrgName || 'Unknown Organization',

          // Flag to indicate this is a licensed track (not owned)
          isLicensedTrack: true,

          // License status for display
          licenseStatus: {
            isLicensed: true,
            durationType: licenseData.licenseType,
            expiryDate: licenseData.dateExpires,
            startDate: licenseData.dateStart,
          },

          // Claimed info
          claimedAt: licenseData.claimedAt,

          // Audio files snapshot from license for playback
          mp3Files: licenseData.mp3Files || [],
          wavFiles: licenseData.wavFiles || [],
          defaultMp3Id: licenseData.defaultMp3Id || null,

          // Default empty arrays for fields expected by TableRows
          tags: [],
          composer: [],
          creativedirector: licenseData.creativeDirectors || [],
          sessionArtists: [],
          flightingCodes: [],
        };
      });

      return licensedTracks;
    } catch (error) {
      console.error('Error fetching licensed tracks:', error);
      return [];
    }
  };

  /**
   * Get pending licenses for an organization
   * These are licenses shared with the org but not yet claimed
   */
  getPendingLicensesForOrg = async (clientOrgId: string): Promise<{
    id: string;
    licenseId: string;
    trackId: string;
    trackName: string;
    sourceOrgId: string;
    sourceOrgName: string;
    licenseType: string;
    dateExpires?: number;
    dateStart?: number;
    sharedAt?: number;
    licensePath: string;
  }[]> => {
    try {
      // Query all licenses where this org is the client and status is pending
      const licensesQuery = query(
        collectionGroup(this.db, 'licenses'),
        where('clientOrgId', '==', clientOrgId),
        where('status', '==', 'pending')
      );

      const querySnapshot = await getDocs(licensesQuery);

      if (querySnapshot.empty) {
        return [];
      }

      const pendingLicenses = querySnapshot.docs.map((licenseDoc) => {
        const licenseData = licenseDoc.data();

        // Parse the path to get sourceOrgId and trackId
        // Path format: organizations/{orgId}/audiometa/{trackId}/licenses/{licenseId}
        const pathSegments = licenseDoc.ref.path.split('/');
        const sourceOrgId = pathSegments[1];
        const trackId = pathSegments[3];

        return {
          id: licenseDoc.id,
          licenseId: licenseDoc.id,
          trackId: trackId,
          trackName: licenseData.trackName || 'Unknown Track',
          sourceOrgId: sourceOrgId,
          sourceOrgName: licenseData.sourceOrgName || 'Unknown Organization',
          licenseType: licenseData.licenseType || 'unknown',
          dateExpires: licenseData.dateExpires,
          dateStart: licenseData.dateStart,
          sharedAt: licenseData.sharedAt,
          licensePath: licenseDoc.ref.path,
        };
      });

      return pendingLicenses;
    } catch (error) {
      console.error('Error fetching pending licenses:', error);
      return [];
    }
  };

  /**
   * Claim a pending license that was shared directly to an org
   * Updates license status and creates audiometa in client org
   */
  claimPendingLicense = async (
    sourceOrgId: string,
    trackId: string,
    licenseId: string,
    claimingOrgId: string,
    claimingUserId: string,
    claimingOrgName?: string
  ): Promise<boolean> => {
    try {
      const licenseRef = doc(
        this.db,
        getOrgCollectionPath(sourceOrgId, AUDIOMETAPATH),
        trackId,
        'licenses',
        licenseId
      );

      // Get the license data
      const licenseSnap = await getDoc(licenseRef);
      const licenseData = licenseSnap.exists() ? licenseSnap.data() : null;

      if (!licenseData) {
        console.error('License not found');
        return false;
      }

      // Verify this license is for the claiming org
      if (licenseData.clientOrgId !== claimingOrgId) {
        console.error('License is not assigned to this organization');
        return false;
      }

      // Update the license status to active (and org name if not already set)
      const updateData: any = {
        status: 'active',
        claimedAt: Date.now(),
        claimedBy: claimingUserId,
      };
      if (claimingOrgName && !licenseData.clientOrgName) {
        updateData.clientOrgName = claimingOrgName;
      }
      await updateDoc(licenseRef, updateData);

      // Create audiometa document in the client org's collection
      await this.createClientAudiometaFromLicense(
        sourceOrgId,
        trackId,
        licenseId,
        claimingOrgId,
        licenseData,
        {
          claimedAt: Date.now(),
          claimedBy: claimingUserId,
        }
      );

      // Notify the artist org (in-app + email). Direct-share, so wasShareLink=false.
      // Best-effort — a notification failure shouldn't fail the claim.
      try {
        const notify = httpsCallable(this.functionsWest1, 'notifyLicenseClaimed');
        await notify({ sourceOrgId, trackId, licenseId, wasShareLink: false });
      } catch (notifyErr) {
        console.error('notifyLicenseClaimed failed (direct share claim):', notifyErr);
      }

      return true;
    } catch (error) {
      console.error('Error claiming pending license:', error);
      return false;
    }
  };

  /**
   * Search for share targets (organizations + global client/agency entries)
   * Returns combined results with indicators for direct sharing vs claim link
   */
  searchShareTargets = async (searchTerm: string, excludeOrgId?: string): Promise<{
    type: 'org' | 'client' | 'agency';
    id: string;
    name: string;
    linkedOrgId: string | null;
    hasOrg: boolean;
  }[]> => {
    if (!searchTerm || searchTerm.length < 2) {
      return [];
    }

    const results: {
      type: 'org' | 'client' | 'agency';
      id: string;
      name: string;
      linkedOrgId: string | null;
      hasOrg: boolean;
    }[] = [];

    const searchLower = searchTerm.toLowerCase();

    try {
      // Search organizations
      const orgsSnapshot = await getDocs(collection(this.db, ORGANIZATIONSPATH));
      orgsSnapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();
        const orgName = data.name || '';
        if (
          orgName.toLowerCase().includes(searchLower) &&
          docSnap.id !== excludeOrgId
        ) {
          results.push({
            type: 'org',
            id: docSnap.id,
            name: orgName,
            linkedOrgId: docSnap.id,
            hasOrg: true,
          });
        }
      });

      // Search global client collection
      const clientsSnapshot = await getDocs(collection(this.db, CLIENTPATH));
      clientsSnapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();
        const clientName = data.label || data.name || '';
        if (clientName.toLowerCase().includes(searchLower)) {
          // Check if already added as org
          const existingOrg = results.find(
            (r) => r.type === 'org' && r.linkedOrgId === data.linkedOrgId
          );
          if (!existingOrg) {
            results.push({
              type: 'client',
              id: docSnap.id,
              name: clientName,
              linkedOrgId: data.linkedOrgId || null,
              hasOrg: !!data.linkedOrgId,
            });
          }
        }
      });

      // Search global agency collection
      const agenciesSnapshot = await getDocs(collection(this.db, AGENCYPATH));
      agenciesSnapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();
        const agencyName = data.label || data.name || '';
        if (agencyName.toLowerCase().includes(searchLower)) {
          // Check if already added as org or client
          const existingOrg = results.find(
            (r) => r.linkedOrgId === data.linkedOrgId && data.linkedOrgId
          );
          if (!existingOrg) {
            results.push({
              type: 'agency',
              id: docSnap.id,
              name: agencyName,
              linkedOrgId: data.linkedOrgId || null,
              hasOrg: !!data.linkedOrgId,
            });
          }
        }
      });

      // Sort results: orgs first, then by name
      results.sort((a, b) => {
        if (a.hasOrg !== b.hasOrg) return a.hasOrg ? -1 : 1;
        return a.name.localeCompare(b.name);
      });

      return results.slice(0, 20); // Limit to 20 results
    } catch (error) {
      console.error('Error searching share targets:', error);
      return [];
    }
  };

  /**
   * Search for organizations and global entries (artists, clients, agencies)
   * Used during org creation to find existing entities to link with
   */
  searchOrganizationsAndEntries = async (searchTerm: string): Promise<{
    type: 'org' | 'artist' | 'client' | 'agency';
    id: string;
    name: string;
    linkedOrgId: string | null;
    hasOrg: boolean;
    orgType?: string;
  }[]> => {
    if (!searchTerm || searchTerm.length < 2) {
      return [];
    }

    const searchLower = searchTerm.toLowerCase();
    const results: {
      type: 'org' | 'artist' | 'client' | 'agency';
      id: string;
      name: string;
      linkedOrgId: string | null;
      hasOrg: boolean;
      orgType?: string;
    }[] = [];

    // Search each collection independently - don't let one failure stop others

    // Search organizations
    try {
      const orgsRef = collection(this.db, ORGANIZATIONSPATH);
      const orgsSnapshot = await getDocs(orgsRef);
      orgsSnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.name?.toLowerCase().includes(searchLower)) {
          results.push({
            type: 'org',
            id: docSnap.id,
            name: data.name,
            linkedOrgId: docSnap.id,
            hasOrg: true,
            orgType: data.type,
          });
        }
      });
    } catch (error) {
      // Continue to next collection
    }

    // Search global artists
    try {
      const artistsRef = collection(this.db, GLOBAL_ARTISTPATH);
      const artistsSnapshot = await getDocs(artistsRef);
      artistsSnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        const artistName = data.label || data.name || '';
        if (artistName.toLowerCase().includes(searchLower)) {
          results.push({
            type: 'artist',
            id: docSnap.id,
            name: artistName,
            linkedOrgId: data.linkedOrgId || null,
            hasOrg: !!data.linkedOrgId,
          });
        }
      });
    } catch (error) {
      // Continue to next collection
    }

    // Search global clients
    try {
      const clientsRef = collection(this.db, CLIENTPATH);
      const clientsSnapshot = await getDocs(clientsRef);
      clientsSnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        const clientName = data.label || data.name || '';
        if (clientName.toLowerCase().includes(searchLower)) {
          results.push({
            type: 'client',
            id: docSnap.id,
            name: clientName,
            linkedOrgId: data.linkedOrgId || null,
            hasOrg: !!data.linkedOrgId,
          });
        }
      });
    } catch (error) {
      // Continue to next collection
    }

    // Search global agencies
    try {
      const agenciesRef = collection(this.db, AGENCYPATH);
      const agenciesSnapshot = await getDocs(agenciesRef);
      agenciesSnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        const agencyName = data.label || data.name || '';
        if (agencyName.toLowerCase().includes(searchLower)) {
          results.push({
            type: 'agency',
            id: docSnap.id,
            name: agencyName,
            linkedOrgId: data.linkedOrgId || null,
            hasOrg: !!data.linkedOrgId,
          });
        }
      });
    } catch (error) {
      // Continue to return results
    }

    // Sort results: unlinked entries first (they can be claimed), then by name
    results.sort((a, b) => {
      if (a.hasOrg !== b.hasOrg) return a.hasOrg ? 1 : -1;
      return a.name.localeCompare(b.name);
    });

    return results.slice(0, 20);
  };

  /**
   * Create a global entry (artist, client, or agency) linked to an organization
   */
  createGlobalEntryForOrg = async (
    orgId: string,
    orgName: string,
    orgType: 'artist' | 'client' | 'agency'
  ): Promise<string> => {
    try {
      let collectionPath: string;
      switch (orgType) {
        case 'artist':
          collectionPath = GLOBAL_ARTISTPATH;
          break;
        case 'client':
          collectionPath = CLIENTPATH;
          break;
        case 'agency':
          collectionPath = AGENCYPATH;
          break;
        default:
          throw new Error(`Invalid org type: ${orgType}`);
      }

      const entryRef = doc(collection(this.db, collectionPath));
      const entryData = {
        label: orgName,
        value: orgName.toLowerCase().replace(/\s+/g, '-'),
        linkedOrgId: orgId,
        createdAt: Date.now(),
      };

      await setDoc(entryRef, entryData);
      return entryRef.id;
    } catch (error) {
      console.error('Error creating global entry:', error);
      throw error;
    }
  };

  /**
   * Link an existing global entry to an organization
   */
  linkGlobalEntryToOrg = async (
    entryType: 'artist' | 'client' | 'agency',
    entryId: string,
    orgId: string
  ): Promise<void> => {
    try {
      let collectionPath: string;
      switch (entryType) {
        case 'artist':
          collectionPath = GLOBAL_ARTISTPATH;
          break;
        case 'client':
          collectionPath = CLIENTPATH;
          break;
        case 'agency':
          collectionPath = AGENCYPATH;
          break;
        default:
          throw new Error(`Invalid entry type: ${entryType}`);
      }

      const entryRef = doc(this.db, collectionPath, entryId);
      await updateDoc(entryRef, {
        linkedOrgId: orgId,
        linkedAt: Date.now(),
      });
    } catch (error) {
      console.error('Error linking global entry to org:', error);
      throw error;
    }
  };

  // ========== LICENSE REQUEST METHODS ==========

  /**
   * Create a license request from a client to an artist
   * Stored in the artist's org under licenseRequests subcollection
   */
  createLicenseRequest = async (
    artistOrgId: string,
    trackId: string,
    trackName: string,
    requestingOrgId: string,
    requestingOrgName: string,
    requestData: {
      licenseType: string;
      dateExpires: number | null;
      dateStart: number | null;
      jobTitle?: string;
      client?: { label: string; value: string } | null;
      agency?: { label: string; value: string } | null;
      flightingPlatform?: { label: string; value: string }[];
      territories?: { label: string; value: string }[];
      notes?: string;
    }
  ): Promise<string> => {
    try {
      const requestsRef = collection(this.db, ORGANIZATIONSPATH, artistOrgId, 'licenseRequests');
      const newRequestRef = doc(requestsRef);

      // Filter out undefined values from requestData (Firestore doesn't accept undefined)
      const cleanedRequestData: Record<string, any> = {};
      for (const [key, value] of Object.entries(requestData)) {
        if (value !== undefined) {
          cleanedRequestData[key] = value;
        }
      }

      // Get the user's name from their Firestore document
      let createdByName = this.auth.currentUser?.email || 'Unknown User';
      if (this.auth.currentUser?.uid) {
        const userDetails = await this.getUserDetails(this.auth.currentUser.uid);
        if (userDetails?.name) {
          createdByName = userDetails.name;
        } else if (userDetails?.username) {
          createdByName = userDetails.username;
        }
      }

      const fullRequestData = {
        id: newRequestRef.id,
        trackId,
        trackName,
        artistOrgId,
        requestingOrgId,
        requestingOrgName,
        ...cleanedRequestData,
        status: 'pending' as const,
        createdAt: Date.now(),
        createdBy: this.auth.currentUser?.uid || '',
        createdByName,
      };

      await setDoc(newRequestRef, fullRequestData);
      return newRequestRef.id;
    } catch (error) {
      console.error('Error creating license request:', error);
      throw error;
    }
  };

  /**
   * Get pending license requests for an artist's organization
   */
  getPendingLicenseRequestsForOrg = async (artistOrgId: string): Promise<{
    id: string;
    trackId: string;
    trackName: string;
    requestingOrgId: string;
    requestingOrgName: string;
    licenseType: string;
    dateExpires: number | null;
    dateStart: number | null;
    jobTitle?: string;
    client?: { label: string; value: string } | null;
    agency?: { label: string; value: string } | null;
    flightingPlatform?: { label: string; value: string }[];
    territories?: { label: string; value: string }[];
    notes?: string;
    createdAt: number;
    status: string;
  }[]> => {
    try {
      const requestsRef = collection(this.db, ORGANIZATIONSPATH, artistOrgId, 'licenseRequests');
      const q = query(
        requestsRef,
        where('status', '==', 'pending'),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as any[];
    } catch (error) {
      console.error('Error fetching pending license requests:', error);
      return [];
    }
  };

  /**
   * Approve a license request - creates a pending license for the client to claim
   */
  approveLicenseRequest = async (
    artistOrgId: string,
    requestId: string
  ): Promise<boolean> => {
    try {
      // Get the request data
      const requestRef = doc(this.db, ORGANIZATIONSPATH, artistOrgId, 'licenseRequests', requestId);
      const requestSnap = await getDoc(requestRef);

      if (!requestSnap.exists()) {
        console.error('License request not found');
        return false;
      }

      const requestData = requestSnap.data();

      // Get track data for the license snapshot
      const trackRef = doc(this.db, getOrgCollectionPath(artistOrgId, AUDIOMETAPATH), requestData.trackId);
      const trackSnap = await getDoc(trackRef);
      const trackData = trackSnap.exists() ? trackSnap.data() : null;

      // Get artist org name
      const orgRef = doc(this.db, ORGANIZATIONSPATH, artistOrgId);
      const orgSnap = await getDoc(orgRef);
      const orgName = orgSnap.exists() ? orgSnap.data().name : 'Unknown Organization';

      // Create the license with pending status (client needs to claim)
      const licenseData = {
        licenseType: requestData.licenseType,
        dateExpires: requestData.dateExpires,
        dateStart: requestData.dateStart || Date.now(),
        jobTitle: requestData.jobTitle || null,
        client: requestData.client || null,
        agency: requestData.agency || null,
        flightingPlatform: requestData.flightingPlatform || [],
        territories: requestData.territories || [],
        notes: requestData.notes || null,
        // Set client org info so they can claim it
        clientOrgId: requestData.requestingOrgId,
        clientOrgName: requestData.requestingOrgName,
        status: 'pending',
        sharedAt: Date.now(),
        // Track and org snapshots
        trackName: requestData.trackName,
        trackId: requestData.trackId,
        sourceOrgId: artistOrgId,
        sourceOrgName: orgName,
        // Audio files and metadata snapshots from track
        mp3Files: trackData?.mp3Files || [],
        wavFiles: trackData?.wavFiles || [],
        defaultMp3Id: trackData?.defaultMp3Id || null,
        key: trackData?.key || null,
        bpm: trackData?.bpm || null,
        genre: trackData?.genre || null,
        tags: trackData?.tags || [],
        composer: trackData?.composer || [],
        artist: trackData?.artist || null,
        album: trackData?.album || null,
        recordLabel: trackData?.recordLabel || null,
        publisher: trackData?.publisher || null,
        sessionArtists: trackData?.sessionArtists || [],
        trackColor: trackData?.trackColor || null,
        // Request reference
        fromRequestId: requestId,
        createdBy: this.auth.currentUser?.uid || '',
      };

      // Create the license
      await this.createLicense(artistOrgId, requestData.trackId, licenseData);

      // Update request status to approved
      await updateDoc(requestRef, {
        status: 'approved',
        approvedAt: Date.now(),
        approvedBy: this.auth.currentUser?.uid || '',
      });

      return true;
    } catch (error) {
      console.error('Error approving license request:', error);
      return false;
    }
  };

  /**
   * Reject a license request
   */
  rejectLicenseRequest = async (
    artistOrgId: string,
    requestId: string,
    rejectionReason?: string
  ): Promise<boolean> => {
    try {
      const requestRef = doc(this.db, ORGANIZATIONSPATH, artistOrgId, 'licenseRequests', requestId);

      await updateDoc(requestRef, {
        status: 'rejected',
        rejectedAt: Date.now(),
        rejectedBy: this.auth.currentUser?.uid || '',
        rejectionReason: rejectionReason || null,
        dismissedByClient: false,
      });

      return true;
    } catch (error) {
      console.error('Error rejecting license request:', error);
      return false;
    }
  };

  /**
   * Get license requests made by a client organization
   * Used to show the client their pending/rejected requests
   */
  getClientLicenseRequests = async (clientOrgId: string): Promise<{
    id: string;
    trackId: string;
    trackName: string;
    artistOrgId: string;
    licenseType: string;
    status: string;
    createdAt: number;
    rejectedAt?: number;
    rejectionReason?: string;
  }[]> => {
    try {
      // Query all licenseRequests subcollections using collection group
      const requestsQuery = query(
        collectionGroup(this.db, 'licenseRequests'),
        where('requestingOrgId', '==', clientOrgId),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(requestsQuery);
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          trackId: data.trackId,
          trackName: data.trackName,
          artistOrgId: data.artistOrgId,
          licenseType: data.licenseType,
          status: data.status,
          createdAt: data.createdAt,
          rejectedAt: data.rejectedAt,
          rejectionReason: data.rejectionReason,
        };
      });
    } catch (error) {
      console.error('Error fetching client license requests:', error);
      return [];
    }
  };

  /**
   * Get license requests for a specific track made by a client
   * Used to show pending requests in the client's license history
   */
  getClientLicenseRequestsForTrack = async (
    artistOrgId: string,
    trackId: string,
    clientOrgId: string
  ): Promise<any[]> => {
    try {
      const requestsRef = collection(this.db, ORGANIZATIONSPATH, artistOrgId, 'licenseRequests');
      const q = query(
        requestsRef,
        where('trackId', '==', trackId),
        where('requestingOrgId', '==', clientOrgId),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (error) {
      console.error('Error fetching client license requests for track:', error);
      return [];
    }
  };

  /**
   * Real-time listener for license requests for a specific track made by a client
   */
  subscribeClientLicenseRequestsForTrack = (
    artistOrgId: string,
    trackId: string,
    clientOrgId: string,
    callback: (requests: any[]) => void
  ) => {
    const requestsRef = collection(this.db, ORGANIZATIONSPATH, artistOrgId, 'licenseRequests');
    const q = query(
      requestsRef,
      where('trackId', '==', trackId),
      where('requestingOrgId', '==', clientOrgId),
      orderBy('createdAt', 'desc')
    );

    return onSnapshot(q, (snapshot) => {
      const requests = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      callback(requests);
    });
  };

  /**
   * Get rejected license requests for a client org that haven't been dismissed
   * Used to notify clients when their requests are rejected
   */
  getRejectedLicenseRequestsForClient = async (clientOrgId: string): Promise<any[]> => {
    try {
      // Query all licenseRequests subcollections using collection group
      // We query for rejected requests and filter client-side for undismissed ones
      // (since documents without dismissedByClient field won't match a where clause)
      const requestsQuery = query(
        collectionGroup(this.db, 'licenseRequests'),
        where('requestingOrgId', '==', clientOrgId),
        where('status', '==', 'rejected'),
        orderBy('rejectedAt', 'desc')
      );

      const querySnapshot = await getDocs(requestsQuery);
      // Filter out dismissed requests client-side
      const results = querySnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
        } as any))
        .filter(request => request.dismissedByClient !== true);
      return results;
    } catch (error) {
      console.error('Error fetching rejected license requests:', error);
      return [];
    }
  };

  /**
   * Dismiss a rejected license request (mark as seen by client)
   */
  dismissRejectedLicenseRequest = async (
    artistOrgId: string,
    requestId: string
  ): Promise<boolean> => {
    try {
      const requestRef = doc(this.db, ORGANIZATIONSPATH, artistOrgId, 'licenseRequests', requestId);
      await updateDoc(requestRef, {
        dismissedByClient: true,
        dismissedAt: Date.now(),
      });
      return true;
    } catch (error) {
      console.error('Error dismissing rejected request:', error);
      return false;
    }
  };

  /**
   * Get expired or revoked licenses for a client org that haven't been acknowledged
   * Used to notify clients when their licenses expire or are deactivated
   */
  getExpiredLicensesForClient = async (clientOrgId: string): Promise<any[]> => {
    try {
      // Query all licenses subcollections for this client
      const licensesQuery = query(
        collectionGroup(this.db, 'licenses'),
        where('clientOrgId', '==', clientOrgId),
        where('status', '==', 'active'), // Only look at licenses that were active
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(licensesQuery);
      const now = Date.now();

      // Filter for expired licenses that haven't been acknowledged
      const expiredLicenses = querySnapshot.docs
        .map(docSnap => {
          const data = docSnap.data();
          // Get the path to determine sourceOrgId and trackId
          const pathParts = docSnap.ref.path.split('/');
          // Path format: organizations/{orgId}/audiometa/{trackId}/licenses/{licenseId}
          const sourceOrgId = pathParts[1];
          const trackId = pathParts[3];

          return {
            id: docSnap.id,
            licenseId: docSnap.id,
            sourceOrgId,
            trackId,
            licensePath: docSnap.ref.path,
            ...data,
          } as any;
        })
        .filter((license: any) => {
          // Check if license is expired and not acknowledged
          const isExpired = license.dateExpires && license.dateExpires < now;
          const isAcknowledged = license.clientAcknowledgedExpiry === true;
          return isExpired && !isAcknowledged;
        });

      return expiredLicenses;
    } catch (error) {
      console.error('Error fetching expired licenses:', error);
      return [];
    }
  };

  /**
   * Get deactivated (revoked) licenses for a client org that haven't been acknowledged
   */
  getDeactivatedLicensesForClient = async (clientOrgId: string): Promise<any[]> => {
    try {
      // Query for deactivated licenses
      const licensesQuery = query(
        collectionGroup(this.db, 'licenses'),
        where('clientOrgId', '==', clientOrgId),
        where('status', '==', 'inactive'),
        orderBy('deactivatedAt', 'desc')
      );

      const querySnapshot = await getDocs(licensesQuery);

      // Filter for deactivated licenses that haven't been acknowledged
      const deactivatedLicenses = querySnapshot.docs
        .map(docSnap => {
          const data = docSnap.data();
          const pathParts = docSnap.ref.path.split('/');
          const sourceOrgId = pathParts[1];
          const trackId = pathParts[3];

          return {
            id: docSnap.id,
            licenseId: docSnap.id,
            sourceOrgId,
            trackId,
            licensePath: docSnap.ref.path,
            ...data,
          } as any;
        })
        .filter((license: any) => {
          // Only show if not acknowledged and was deactivated (not just created as inactive)
          const isAcknowledged = license.clientAcknowledgedExpiry === true;
          const wasDeactivated = license.deactivatedAt != null;
          return wasDeactivated && !isAcknowledged;
        });

      return deactivatedLicenses;
    } catch (error) {
      console.error('Error fetching deactivated licenses:', error);
      return [];
    }
  };

  /**
   * Acknowledge an expired or deactivated license — dismisses the in-app
   * notification for the specified side of the license.
   *
   * Sets one of two independent flags:
   *   - clientAcknowledgedExpiry (recipientRole='client') — the license holder's dismiss
   *   - artistAcknowledgedExpiry (recipientRole='artist') — the license issuer's dismiss
   */
  acknowledgeExpiredLicense = async (
    sourceOrgId: string,
    trackId: string,
    licenseId: string,
    recipientRole: 'client' | 'artist' = 'client',
  ): Promise<boolean> => {
    try {
      const licenseRef = doc(
        this.db,
        getOrgCollectionPath(sourceOrgId, AUDIOMETAPATH),
        trackId,
        'licenses',
        licenseId
      );
      const now = Date.now();
      const update = recipientRole === 'artist'
        ? { artistAcknowledgedExpiry: true, artistAcknowledgedAt: now }
        : { clientAcknowledgedExpiry: true, clientAcknowledgedAt: now };
      await updateDoc(licenseRef, update);
      return true;
    } catch (error) {
      console.error('Error acknowledging expired license:', error);
      return false;
    }
  };

  // =====================================================
  // PUBLIC CATALOGUE METHODS
  // =====================================================

  /**
   * Publish a track to the public catalogue
   * Creates a lean copy with only necessary fields for marketplace browsing
   */
  publishToPublicCatalogue = async (
    sourceOrgId: string,
    sourceOrgName: string,
    trackId: string,
    trackData: any
  ): Promise<boolean> => {
    try {
      const publicDoc = {
        // Source reference
        sourceOrgId,
        sourceTrackId: trackId,
        sourceOrgName,

        // Core track info for filtering/display
        trackName: trackData.trackName || '',
        artist: trackData.artist || null,
        album: trackData.album || null,
        genre: trackData.genre || null,
        tags: trackData.tags || [],
        key: trackData.key || null,
        bpm: trackData.bpm || null,

        // Audio files (reference to original - not duplicated)
        mp3Files: trackData.mp3Files || [],
        waveformUrl: trackData.waveformUrl || null,
        trackColor: trackData.trackColor || null,

        // Publishing metadata
        publishedAt: Date.now(),
        isPublic: true,
        isHiddenByExclusiveLicense: false,
        exclusiveLicenseExpiresAt: null,
      };

      const publicRef = doc(this.db, PUBLIC_CATALOGUEPATH, trackId);
      await setDoc(publicRef, publicDoc);

      // Update the source audiometa to mark as public
      const sourceRef = doc(this.db, getOrgCollectionPath(sourceOrgId, AUDIOMETAPATH), trackId);
      await updateDoc(sourceRef, { isPublic: true });

      console.log('Track published to public catalogue:', trackId);
      return true;
    } catch (error) {
      console.error('Error publishing to public catalogue:', error);
      return false;
    }
  };

  /**
   * Unpublish a track from the public catalogue
   * Deletes the public catalogue document
   */
  unpublishFromPublicCatalogue = async (
    sourceOrgId: string,
    trackId: string
  ): Promise<boolean> => {
    try {
      const publicRef = doc(this.db, PUBLIC_CATALOGUEPATH, trackId);
      await deleteDoc(publicRef);

      // Update the source audiometa to mark as not public
      const sourceRef = doc(this.db, getOrgCollectionPath(sourceOrgId, AUDIOMETAPATH), trackId);
      await updateDoc(sourceRef, { isPublic: false });

      console.log('Track unpublished from public catalogue:', trackId);
      return true;
    } catch (error) {
      console.error('Error unpublishing from public catalogue:', error);
      return false;
    }
  };

  /**
   * Update a track in the public catalogue (when source track is edited)
   * Only updates if the track is currently public
   */
  updatePublicCatalogueTrack = async (
    sourceOrgId: string,
    sourceOrgName: string,
    trackId: string,
    trackData: any
  ): Promise<boolean> => {
    try {
      const publicRef = doc(this.db, PUBLIC_CATALOGUEPATH, trackId);
      const publicSnap = await getDoc(publicRef);

      if (!publicSnap.exists()) {
        // Track is not in public catalogue, nothing to update
        return true;
      }

      const existingData = publicSnap.data();

      const updateData = {
        // Update core track info
        trackName: trackData.trackName || '',
        artist: trackData.artist || null,
        album: trackData.album || null,
        genre: trackData.genre || null,
        tags: trackData.tags || [],
        key: trackData.key || null,
        bpm: trackData.bpm || null,

        // Update audio files reference
        mp3Files: trackData.mp3Files || [],
        waveformUrl: trackData.waveformUrl || null,
        trackColor: trackData.trackColor || null,

        // Keep existing publishing metadata
        sourceOrgName,
        updatedAt: Date.now(),

        // Preserve exclusive license state
        isHiddenByExclusiveLicense: existingData.isHiddenByExclusiveLicense || false,
        exclusiveLicenseExpiresAt: existingData.exclusiveLicenseExpiresAt || null,
      };

      await updateDoc(publicRef, updateData);
      console.log('Public catalogue track updated:', trackId);
      return true;
    } catch (error) {
      console.error('Error updating public catalogue track:', error);
      return false;
    }
  };

  /**
   * Get a track from the public catalogue
   */
  getPublicCatalogueTrack = async (trackId: string): Promise<any | null> => {
    try {
      const publicRef = doc(this.db, PUBLIC_CATALOGUEPATH, trackId);
      const publicSnap = await getDoc(publicRef);

      if (!publicSnap.exists()) {
        return null;
      }

      return { id: publicSnap.id, ...publicSnap.data() };
    } catch (error) {
      console.error('Error getting public catalogue track:', error);
      return null;
    }
  };

  /**
   * Set track hidden status due to exclusive license
   * Called when an exclusive license is created or expires/revoked
   */
  setTrackHiddenByExclusiveLicense = async (
    trackId: string,
    isHidden: boolean,
    expiresAt: number | null = null
  ): Promise<boolean> => {
    try {
      const publicRef = doc(this.db, PUBLIC_CATALOGUEPATH, trackId);
      const publicSnap = await getDoc(publicRef);

      if (!publicSnap.exists()) {
        // Track is not in public catalogue
        return true;
      }

      await updateDoc(publicRef, {
        isHiddenByExclusiveLicense: isHidden,
        exclusiveLicenseExpiresAt: expiresAt,
      });

      console.log(`Public catalogue track ${isHidden ? 'hidden' : 'unhidden'} by exclusive license:`, trackId);
      return true;
    } catch (error) {
      console.error('Error setting track hidden by exclusive license:', error);
      return false;
    }
  };

  /**
   * Create a notification for when an exclusive license ends
   * This prompts the artist to make the track public again
   */
  createExclusiveLicenseEndedNotification = async (
    orgId: string,
    trackId: string,
    licenseData: any,
    type: 'revoked' | 'expired' = 'revoked'
  ): Promise<boolean> => {
    try {
      // Store notification in org's notifications subcollection
      const notificationsRef = collection(this.db, ORGANIZATIONSPATH, orgId, 'exclusiveLicenseNotifications');
      const notificationDoc = doc(notificationsRef);

      await setDoc(notificationDoc, {
        id: notificationDoc.id,
        trackId,
        trackName: licenseData?.trackName || 'Unknown Track',
        licenseId: licenseData?.id,
        clientName: licenseData?.client?.label || 'Unknown Client',
        endedAt: Date.now(),
        type,
        dismissed: false,
        republished: false,
        createdAt: Date.now(),
      });

      return true;
    } catch (error) {
      console.error('Error creating exclusive license notification:', error);
      return false;
    }
  };

  /**
   * Get pending exclusive license notifications for an org
   */
  getExclusiveLicenseNotifications = async (orgId: string): Promise<any[]> => {
    try {
      const notificationsRef = collection(this.db, ORGANIZATIONSPATH, orgId, 'exclusiveLicenseNotifications');
      const q = query(
        notificationsRef,
        where('dismissed', '==', false),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error fetching exclusive license notifications:', error);
      return [];
    }
  };

  /**
   * Dismiss an exclusive license notification
   */
  dismissExclusiveLicenseNotification = async (
    orgId: string,
    notificationId: string,
    republish: boolean = false
  ): Promise<boolean> => {
    try {
      const notificationRef = doc(
        this.db,
        ORGANIZATIONSPATH,
        orgId,
        'exclusiveLicenseNotifications',
        notificationId
      );
      await updateDoc(notificationRef, {
        dismissed: true,
        republished: republish,
        dismissedAt: Date.now(),
      });
      return true;
    } catch (error) {
      console.error('Error dismissing exclusive license notification:', error);
      return false;
    }
  };

  /**
   * Check if a track has an active exclusive license
   * Returns the license details if one exists, null otherwise
   */
  getActiveExclusiveLicense = async (
    sourceOrgId: string,
    trackId: string
  ): Promise<any | null> => {
    try {
      const licensesRef = collection(
        this.db,
        getOrgCollectionPath(sourceOrgId, AUDIOMETAPATH),
        trackId,
        'licenses'
      );

      const now = Date.now();
      const licensesQuery = query(
        licensesRef,
        where('isExclusive', '==', true),
        where('status', '==', 'active')
      );

      const querySnapshot = await getDocs(licensesQuery);

      // Find an exclusive license that hasn't expired
      for (const docSnap of querySnapshot.docs) {
        const license = docSnap.data();
        const isExpired = license.dateExpires && license.dateExpires < now;
        if (!isExpired) {
          return { id: docSnap.id, ...license };
        }
      }

      return null;
    } catch (error) {
      console.error('Error checking for active exclusive license:', error);
      return null;
    }
  };

  /**
   * Get all visible tracks from the public catalogue
   * Filters out tracks hidden by exclusive licenses
   */
  getPublicCatalogueTracks = async (): Promise<any[]> => {
    try {
      const publicRef = collection(this.db, PUBLIC_CATALOGUEPATH);
      const publicQuery = query(
        publicRef,
        where('isPublic', '==', true),
        where('isHiddenByExclusiveLicense', '==', false),
        orderBy('publishedAt', 'desc')
      );

      const querySnapshot = await getDocs(publicQuery);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching public catalogue tracks:', error);
      return [];
    }
  };

  /**
   * Get public catalogue collection reference for real-time listeners
   */
  getPublicCatalogueRef = () => {
    return collection(this.db, PUBLIC_CATALOGUEPATH);
  };
}

// Create singleton instance and expose on window for console access (dev/admin use)
const firebaseInstance = new Firebase();
if (typeof window !== 'undefined') {
  (window as any).firebaseAdmin = firebaseInstance;
}

export default Firebase;
